import React, { Component, ReactNode, ErrorInfo } from 'react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { RefreshCcw, AlertCircle } from 'lucide-react'
import { QueryClient } from '@tanstack/react-query'
import { sentryUtils } from '@/lib/sentry'
import { useTranslation } from '@/hooks/useTranslation'

interface Props {
	children: ReactNode
	fallback?: ReactNode
	queryClient?: QueryClient
}

interface State {
	hasError: boolean
	error: Error | null
	errorInfo: ErrorInfo | null
	retryCount: number
}

interface ErrorMessageProps {
	isLoadingError: boolean
	error: Error | null
	errorInfo: ErrorInfo | null
	onRetry: () => void
	retryCount: number
}

const ErrorMessage: React.FC<ErrorMessageProps> = ({
	isLoadingError,
	error,
	errorInfo,
	onRetry,
	retryCount,
}) => {
	const { t } = useTranslation()
	
	return (
		<div className="min-h-screen flex items-center justify-center p-4">
			<div className="max-w-md w-full">
				<Alert variant="destructive" className="border-2">
					<AlertCircle className="h-5 w-5" />
					<AlertTitle className="text-lg font-semibold">
						{isLoadingError ? t('errorBoundary.loadingError') : t('errorBoundary.somethingWentWrong')}
					</AlertTitle>
					<AlertDescription className="mt-3 space-y-3">
						<p className="text-sm">
							{isLoadingError
								? t('errorBoundary.loadingErrorMessage')
								: t('errorBoundary.unexpectedError')}
						</p>
						{error && (
							<details className="text-xs">
								<summary className="cursor-pointer font-medium">
									{t('errorBoundary.errorDetails')}
								</summary>
								<pre className="mt-2 whitespace-pre-wrap break-words bg-destructive/10 p-2 rounded">
									{error.toString()}
									{errorInfo && (
										<>
											{'\n\n'}{t('errorBoundary.componentStack')}
											{errorInfo.componentStack}
										</>
									)}
								</pre>
							</details>
						)}
						<div className="flex gap-2 pt-2">
							<Button
								onClick={onRetry}
								variant="default"
								size="sm"
								className="flex-1">
								<RefreshCcw className="h-4 w-4 mr-1" />
								{retryCount >= 2 ? t('errorBoundary.reloadPage') : t('errorBoundary.tryAgain')}
							</Button>
							<Button
								onClick={() => window.history.back()}
								variant="outline"
								size="sm"
								className="flex-1">
								{t('errorBoundary.goBack')}
							</Button>
						</div>
					</AlertDescription>
				</Alert>
			</div>
		</div>
	)
}

const ReloadingMessage: React.FC = () => {
	const { t } = useTranslation()
	
	return (
		<div className="min-h-screen flex items-center justify-center">
			<div className="text-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
				<p className="mt-2 text-sm text-muted-foreground">{t('errorBoundary.reloading')}</p>
			</div>
		</div>
	)
}

function isHotReloadError(error: Error | null) {
	if (!import.meta.hot || !error) return false
	return (
		error.name === 'ReferenceError' ||
		error.message?.includes('useAuth') ||
		error.message?.includes('AuthProvider')
	)
}

export class ErrorBoundary extends Component<Props, State> {
	constructor(props: Props) {
		super(props)
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
			retryCount: 0,
		}
	}

	componentDidMount() {
		if (!import.meta.hot) return
		import.meta.hot.on('vite:afterUpdate', this.handleHotUpdate)
	}

	componentWillUnmount() {
		import.meta.hot?.off('vite:afterUpdate', this.handleHotUpdate)
	}

	handleHotUpdate = () => {
		if (this.state.hasError) {
			this.handleReset()
		}
	}

	static getDerivedStateFromError(error: Error): State {
		if (isHotReloadError(error)) {
			console.warn('Hot reload error detected, will auto-retry')
		}

		return {
			hasError: true,
			error,
			errorInfo: null,
			retryCount: 0,
		}
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error('ErrorBoundary caught an error:', error, errorInfo)

		this.setState({
			error,
			errorInfo,
		})

		if (!isHotReloadError(error)) {
			sentryUtils.captureError(error, {
				errorBoundary: true,
				componentStack: errorInfo.componentStack,
				errorInfo,
			})
		}

		if (isHotReloadError(error)) {
			setTimeout(() => {
				this.handleReset()
			}, 100)
		}
	}

	handleReset = async () => {
		// Clear React Query cache if available
		if (this.props.queryClient) {
			await this.props.queryClient.clear()
		}

		// Reset error state
		this.setState({
			hasError: false,
			error: null,
			errorInfo: null,
			retryCount: this.state.retryCount + 1,
		})

		// Force a page reload after 3 retries
		if (this.state.retryCount >= 2) {
			window.location.reload()
		}
	}

	render() {
		if (this.state.hasError) {
			if (isHotReloadError(this.state.error)) {
				return <ReloadingMessage />
			}

			if (this.props.fallback) {
				return this.props.fallback
			}

			const isLoadingError =
				this.state.error?.message?.toLowerCase().includes('loading') ||
				this.state.error?.message?.toLowerCase().includes('fetch') ||
				this.state.error?.message?.toLowerCase().includes('network')

			return (
				<ErrorMessage
					isLoadingError={isLoadingError}
					error={this.state.error}
					errorInfo={this.state.errorInfo}
					onRetry={this.handleReset}
					retryCount={this.state.retryCount}
				/>
			)
		}

		return this.props.children
	}
}
